import { apiClient } from './apiClient';
import {
  User,
  UserFormData,
  UserFilters,
  UserListResponse,
  UserResponse,
  ResetPasswordResponse,
} from '../types/user';

class UserService {
  private api = apiClient;

  /**
   * Get list of users with pagination and filters
   */
  async getUsers(
    page: number = 1,
    limit: number = 20,
    filters?: UserFilters
  ): Promise<UserListResponse> {
    const params: Record<string, unknown> = { page, limit };

    if (filters) {
      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;
      if (filters.include_deleted) params.include_deleted = 'true';
    }

    const response = await this.api.get<UserListResponse>('/users', { params });
    return response.data;
  }

  /**
   * Get single user by ID
   */
  async getUserById(id: string): Promise<User> {
    const response = await this.api.get<UserResponse>(`/users/${id}`);
    return response.data.data.user;
  }

  /**
   * Create new user
   */
  async createUser(data: UserFormData): Promise<{ user: User; temporaryPassword: string }> {
    const response = await this.api.post<UserResponse>('/users', data);
    return {
      user: response.data.data.user,
      temporaryPassword: response.data.data.temporaryPassword || '',
    };
  }

  /**
   * Update existing user
   */
  async updateUser(id: string, data: Partial<UserFormData>): Promise<User> {
    const response = await this.api.put<UserResponse>(`/users/${id}`, data);
    return response.data.data.user;
  }

  /**
   * Soft-delete user
   */
  async deleteUser(id: string): Promise<void> {
    await this.api.delete(`/users/${id}`);
  }

  /**
   * Restore soft-deleted user (Super Admin only)
   */
  async restoreUser(id: string): Promise<User> {
    const response = await this.api.post<UserResponse>(`/users/${id}/restore`);
    return response.data.data.user;
  }

  /**
   * Reset user password (generates new temporary password)
   */
  async resetUserPassword(id: string): Promise<string> {
    const response = await this.api.post<ResetPasswordResponse>(`/users/${id}/reset-password`);
    return response.data.data.temporaryPassword;
  }

  /**
   * Get all users (for dropdowns, no pagination)
   */
  async getAllUsers(): Promise<User[]> {
    const response = await this.api.get<UserListResponse>('/users', {
      params: { limit: 1000 }, // Large limit to get all users
    });
    return response.data.data.users;
  }
}

export const userService = new UserService();
